import React from 'react';
import { useTranslation } from 'react-i18next';
import { FiThermometer, FiWind, FiDroplet } from 'react-icons/fi';
import { FaLocationArrow } from 'react-icons/fa';
import { getFlightSafetyStatus, getFlightStatusLabel } from '../../utils/flightSafety';
import ConnectionStatus from '../ConnectionStatus';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const StationDashboardHeader = ({ takeOffSite, stationData, now, onRefresh, isRefreshing = false, lastUpdate = null }) => {
    const { t, i18n } = useTranslation();

    // Get flight safety status
    const flightStatus = getFlightSafetyStatus(takeOffSite, now);
    const { status } = flightStatus;

    // Format last update time
    const lastUpdateTime = now?.SEND_TIME || stationData?.last_update;
    const formattedTime = lastUpdateTime ? dayjs(lastUpdateTime).locale(i18n.language).fromNow() : '';

    // Get status color classes
    const getStatusColorClasses = () => {
        switch (status) {
            case 'good':
                return 'bg-green-500 text-white';
            case 'danger':
                return 'bg-orange-500 text-white';
            case 'offline':
                return 'bg-red-500 text-white';
            default:
                return 'bg-gray-500 text-white';
        }
    };

    return (
        <div className="sticky top-14 sm:top-16 z-40 bg-gray-50/95 dark:bg-slate-900/95 backdrop-blur-sm border-b border-gray-200 dark:border-slate-700">
            <div className="px-3 sm:px-4 py-2 sm:py-3">
                <div className="flex items-center justify-between">
                    {/* Status badge + site name. The status badge is hidden on
                        mobile — it wrapped to two lines and broke the header
                        layout, and the same info is conveyed on the Overview
                        cards. */}
                    <div className="flex items-center gap-3 min-w-0">
                        <div className={`hidden sm:block px-3 py-1 rounded-full text-xs font-medium ${getStatusColorClasses()}`}>
                            {t(getFlightStatusLabel(status))}
                        </div>
                        {/* Site name */}
                        <h1 className="text-sm sm:text-lg font-bold text-gray-900 dark:text-white truncate">
                            {t(takeOffSite?.name)}
                        </h1>
                    </div>

                    {/* Quick stats */}
                    <div className="flex items-center space-x-3 sm:space-x-4 text-sm flex-shrink-0">
                        {/* Wind */}
                        <div className="flex items-center space-x-2">
                            <div className="flex items-center space-x-1 text-gray-600 dark:text-gray-400">
                                <FiWind className="w-4 h-4" />
                                <FaLocationArrow
                                    style={{ transform: `rotate(${parseInt(now?.WIND_ANG) + 135 || 135}deg)` }}
                                    className="w-3 h-3"
                                />
                            </div>
                            <span className={`font-medium whitespace-nowrap ${now?.WIND_SP > 7 ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}`}>
                                {Math.round(now?.WIND_SP || 0)} m/s
                            </span>
                        </div>

                        {/* Temperature */}
                        <div className="flex items-center space-x-1">
                            <FiThermometer className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                            <span className="font-medium whitespace-nowrap text-gray-900 dark:text-white">
                                {Math.round(now?.TEMPERATURE || 0)}°C
                            </span>
                        </div>

                        {/* Humidity - Desktop only */}
                        <div className="hidden sm:flex items-center space-x-1">
                            <FiDroplet className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                            <span className="font-medium text-gray-900 dark:text-white">
                                {Math.round(now?.MOIST || 0)}%
                            </span>
                        </div>
                    </div>
                </div>

                {/* Last update and location info */}
                <div className="flex items-center justify-between mt-2 text-xs text-gray-600 dark:text-gray-400">
                    <div className="flex items-center space-x-3">
                        <span>{t('Updated')} {formattedTime}</span>
                        <span className="hidden sm:inline">•</span>
                        <span className="hidden sm:inline">
                            {stationData?.LAT}, {stationData?.LONG} • {stationData?.ALT}m AMSL
                        </span>
                    </div>
                    <ConnectionStatus lastUpdate={lastUpdate} isRefreshing={isRefreshing} />
                </div>
            </div>
        </div>
    );
};

export default StationDashboardHeader;
